import { useEffect, useState, type FormEvent } from "react";
import { Link, useLocation } from "react-router-dom";
import type { LiveComment } from "@tih/shared";
import { useAuth } from "../context/AuthContext";
import { useSiteCopy } from "../context/SiteCopyContext";
import { getAtPath } from "../lib/siteCopyPaths";
import {
  ApiError,
  deleteLiveComment,
  fetchLiveComments,
  postLiveComment,
} from "../api/client";

const POLL_MS = 8000;
const MAX_LEN = 1000;

function formatTime(iso: string): string {
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const sec = Math.round(diff / 1000);
  if (sec < 10) return "just now";
  if (sec < 60) return `${sec}s ago`;
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hr ago`;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? parts[parts.length - 1][0] : "";
  return (first + last).toUpperCase() || "?";
}

export function LiveComments() {
  const { user, isAdmin } = useAuth();
  const { copy } = useSiteCopy();
  const location = useLocation();
  const [comments, setComments] = useState<LiveComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingNote, setPendingNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let timer: number | undefined;

    async function poll() {
      try {
        const next = await fetchLiveComments();
        if (!cancelled) {
          setComments(next);
          setLoading(false);
        }
      } catch {
        if (!cancelled) setLoading(false);
      } finally {
        if (!cancelled) timer = window.setTimeout(poll, POLL_MS);
      }
    }
    poll();
    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, []);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user) return;
    const trimmed = body.trim();
    if (!trimmed) return;
    setBusy(true);
    setError(null);
    setPendingNote(null);
    try {
      await postLiveComment({ body: trimmed });
      setBody("");
      setPendingNote(
        String(
          getAtPath(copy, "live.commentPendingNote") ??
            "Thank you — your comment will appear after a moderator approves it.",
        ),
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not post your comment");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(id: number) {
    const before = comments;
    setComments((cs) => cs.filter((c) => c.id !== id));
    try {
      await deleteLiveComment(id);
    } catch {
      setComments(before);
    }
  }

  return (
    <section className="live-comments" aria-label="Comments">
      <header className="live-comments-head">
        <h2>
          {comments.length} {comments.length === 1 ? "Comment" : "Comments"}
        </h2>
      </header>

      {user ? (
        <form className="live-comment-form" onSubmit={onSubmit}>
          <div className="lc-avatar" aria-hidden="true">{initials(user.name)}</div>
          <div className="lc-form-body">
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={MAX_LEN}
              rows={2}
              placeholder="Add a comment…"
              disabled={busy}
            />
            <div className="lc-form-row">
              <span className="lc-count">{body.length}/{MAX_LEN}</span>
              <div className="lc-form-actions">
                <button
                  type="button"
                  className="btn btn-ghost"
                  disabled={busy || !body}
                  onClick={() => setBody("")}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={busy || !body.trim()}
                >
                  {busy ? "Posting…" : "Comment"}
                </button>
              </div>
            </div>
            {error && <div className="form-error">{error}</div>}
            {pendingNote && (
              <div className="form-success" style={{ padding: 10 }}>
                {pendingNote}
              </div>
            )}
          </div>
        </form>
      ) : (
        <div className="live-comment-signin">
          <Link
            to={`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`}
            className="btn btn-primary"
          >
            Sign in to comment
          </Link>
          <span className="lc-signin-hint">
            Bring a verse, a prayer, a hello — your comment shows up here for everyone in the room.
          </span>
        </div>
      )}

      {loading ? (
        <div className="live-comments-empty">Loading the room…</div>
      ) : comments.length === 0 ? (
        <div className="live-comments-empty">
          No comments yet. Be the first to greet the room.
        </div>
      ) : (
        <ul className="live-comment-list">
          {comments.map((c) => {
            const canDelete =
              !!user && (isAdmin || (c.authorId != null && c.authorId === user.id));
            return (
              <li className="live-comment" key={c.id}>
                <div className="lc-avatar" aria-hidden="true">
                  {initials(c.authorName)}
                </div>
                <div className="lc-body">
                  <div className="lc-meta">
                    <span className="lc-author">{c.authorName}</span>
                    {c.source === "YOUTUBE" && (
                      <span className="lc-source">YouTube</span>
                    )}
                    <span className="lc-time">{formatTime(c.createdAt)}</span>
                    {canDelete && (
                      <button
                        type="button"
                        className="lc-delete"
                        onClick={() => onDelete(c.id)}
                        aria-label="Delete comment"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                  <p className="lc-text">{c.body}</p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
